import type { ParticleMaterial, AudioReactiveLevel } from './types'

// ── Physics presets ──

/** Physics presets mapping names to spring/friction pairs. */
export const PHYSICS = {
  IDLE:       { spring: 0.04, friction: 0.94 },
  STANDARD:   { spring: 0.08, friction: 0.85 },
  SNAPPY:     { spring: 0.15, friction: 0.80 },
  FLUID:      { spring: 0.08, friction: 0.92 },
  GENTLE:     { spring: 0.03, friction: 0.94 },
} as const

// ── DPR caps ──
/** Maximum device pixel ratio for desktop canvases. */
export const DPR_CAP_DESKTOP = 1.5
/** Maximum device pixel ratio for mobile canvases. */
export const DPR_CAP_MOBILE  = 1.1

// ── Particle count tiers ──
/** Performance tier descriptor: particle count, trail length, and bloom skip interval. */
export interface ParticleTier {
  count: number; trailLength: number; bloomInterval: number
}
export const TIERS: Record<string, ParticleTier> = {
  ULTRA:   { count: 3000, trailLength: 14, bloomInterval: 3 },
  HIGH:    { count: 2200, trailLength: 10, bloomInterval: 3 },
  MEDIUM:  { count: 1200, trailLength: 8,  bloomInterval: 4 },
  LOW:     { count: 450,  trailLength: 5,  bloomInterval: 5 },
  MINIMAL: { count: 250,  trailLength: 3,  bloomInterval: 6 },
}

/**
 * Select a performance tier based on viewport width, mobile flag, DPR, and FPS.
 * @param w — Viewport width in CSS pixels
 * @param mobile — Whether the device is mobile
 * @param dpr — Device pixel ratio
 * @param fps — Current measured FPS
 * @returns The best matching {@link ParticleTier}
 */
export function selectTier(w: number, mobile: boolean, dpr: number, fps: number): ParticleTier {
  if (mobile || w < 768) return (dpr > 2 ? TIERS.LOW : TIERS.MINIMAL)!
  if (w < 1200 || fps < 30) return TIERS.MEDIUM!
  if (w < 1920 || fps < 45) return TIERS.HIGH!
  return TIERS.ULTRA!
}

/** Target frame budget in milliseconds for 60fps (16.67ms). */
export const FRAME_BUDGET_MS = 16.67

// ── Performance budget constants ──
/** CPU time budget breakdown for desktop render pipeline stages. */
export const PERF_BUDGET = {
  targetMs: 16.67,
  mobileTargetMs: 33.33,
  headroomFactor: 0.75,
} as const

/** Per-frame time allocation per render stage (desktop). */
export const PERF_BREAKDOWN = {
  clearCanvas: 0.1,
  ambientLayer: 0.3,
  constellationLines: 0.1,
  particleBody: 8.0,
  trailDraw: 2.0,
  overlays: 0.2,
  bloom: 2.5,
} as const

/** Per-frame time allocation per render stage (mobile). */
export const MOBILE_PERF_BREAKDOWN = {
  clearCanvas: 0.1,
  ambientLayer: 0.2,
  constellationLines: 0.0,
  particleBody: 4.0,
  trailDraw: 1.0,
  overlays: 0.2,
  bloom: 1.2,
} as const

// ── Tier decision thresholds ──
/** Thresholds used by {@link selectTier} to decide tier boundaries. */
export const TIER_THRESHOLDS = {
  ULTRA:  { minWidth: 1920, minFps: 45, maxDpr: 1.5 },
  HIGH:   { minWidth: 1200, minFps: 30, maxDpr: 1.5 },
  MEDIUM: { minWidth: 768,  minFps: 30, maxDpr: 2.0 },
  LOW:    { maxWidth: 768,  maxDpr: 2.0 },
  MINIMAL:{ emergencyFps: 30, sustainedFrames: 120 },
} as const

// ── LOD constants ──
/** Level-of-detail degradation parameters for adaptive quality. */
export const LOD = {
  minFactor: 0.2,
  deficitMultiplier: 1.5,
  lowFpsThreshold: 30,
  sustainedLowFrames: 120,
} as const

// ── Seeded PRNG ──
/**
 * Linear congruential pseudo-random number generator.
 * Produces deterministic results for the same seed (used in tests).
 * @param seed — Integer seed value
 * @returns Pseudo-random float in [0, 1)
 */
export function seededRandom(seed: number): number {
  return ((1664525 * seed + 1013904223) >>> 0) / 4294967296
}

// ── Projection math ──
/**
 * 3D → 2D perspective projection.
 * @param x — X coordinate in 3D space
 * @param y — Y coordinate in 3D space
 * @param z — Z coordinate (depth, positive = further away)
 * @param fl — Focal length (default 400)
 * @returns Projected {x, y, scale} in 2D canvas space
 */
export function perspectiveProject(
  x: number, y: number, z: number, fl = 400
): { x: number; y: number; scale: number } {
  const s = fl / (fl + z)
  return { x: x * s, y: y * s, scale: s }
}

/**
 * Convert latitude/longitude to a 3D unit vector on a sphere.
 * @param lat — Latitude in degrees
 * @param lng — Longitude in degrees
 * @param r — Sphere radius
 * @returns {x, y, z} Cartesian coordinates
 */
export function latLngToVector3(lat: number, lng: number, r: number) {
  const p = (90 - lat) * Math.PI / 180, t = (lng + 180) * Math.PI / 180
  return {
    x: -(r * Math.sin(p) * Math.cos(t)),
    y:  r * Math.cos(p),
    z:   r * Math.sin(p) * Math.sin(t),
  }
}

/**
 * Map a Z depth value to an alpha multiplier for depth sorting.
 * @param z — Current Z depth
 * @param zRange — {min, max} depth bounds
 * @returns Alpha in [0.3, 1.0]
 */
export function zToAlpha(z: number, zRange: { min: number; max: number }): number {
  const t = Math.max(0, Math.min(1, (z - zRange.min) / (zRange.max - zRange.min)))
  return 0.3 + (1 - t) * 0.7
}

/**
 * Map a Z depth value to a scale multiplier for depth sorting.
 * @param z — Current Z depth
 * @param zRange — {min, max} depth bounds
 * @returns Scale in [0.5, 1.0]
 */
export function zToScale(z: number, zRange: { min: number; max: number }): number {
  const t = Math.max(0, Math.min(1, (z - zRange.min) / (zRange.max - zRange.min)))
  return 0.5 + (1 - t) * 0.5
}

// ── Lifecycle defaults ──
/** Default lifecycle parameters for particle spawn/age/death. */
export const LIFECYCLE = {
  spawnRadius: 200,
  baseRadius: { min: 0.2, max: 0.6 },
  baseAlpha:  { min: 0.4, max: 0.9 },
  maxLifeBase: 1440,
  maxLifeRange: 1440,
  birthFrames: 30,
  deathFrames: 20,
  radSmoothing: 0.2,
  scaleSmoothing: 0.15,
} as const

// ── Mouse / drift ──
/** Mouse interaction force constants. */
export const MOUSE = {
  influenceFraction: 0.26,
  repulsion: 0.22,
  swirl: 0.08,
} as const

/** Global ambient drift force magnitude. */
export const DRIFT = { force: 0.008 } as const

// ── Visual contract ──
/** Cross-module canonical contract describing visual behaviour parameters. */
export interface VisualContract {
  alphaRange: { min: number; max: number }
  physicsPreset: keyof typeof PHYSICS
  depthEnabled: boolean
  audioReactive: AudioReactiveLevel
  modeAware: boolean
  defaultMaterial: ParticleMaterial
}

/** Default visual contract settings. */
export const DEFAULT_CONTRACT: VisualContract = {
  alphaRange: { min: 0.3, max: 0.9 },
  physicsPreset: 'STANDARD',
  depthEnabled: false,
  audioReactive: 'subtle',
  modeAware: true,
  defaultMaterial: 'soft',
}

// ── Particle defaults (replace magic numbers in particle.ts) ──
/** Sensible default ranges for all particle configuration fields. */
export const PARTICLE_DEFAULTS = {
  springStiffnessMin: 0.03,
  springStiffnessMax: 0.20,
  frictionMin: 0.80,
  frictionMax: 0.94,
  noiseMax: 0.15,
  radiusVariance: 0.3,
  baseRadiusMin: 0.2,
  baseRadiusMax: 0.6,
  baseAlphaMin: 0.4,
  baseAlphaMax: 0.9,
  maxLifeBase: 1440,
  maxLifeRange: 1440,
  birthFrames: 30,
  deathFrames: 20,
} as const
